import {
  BenefitFormData,
  CircuitDayFormData,
} from '@/types/circuits/CircuitDaysFormData';
import { differenceInYears, startOfToday } from 'date-fns';
import { useMemo, useState } from 'react';
import { SelectedEstimateOption } from '@/types/estimates/SelectedEstimateOptionType';
import { roundUpPrice, sanitizeDeltaPrice } from '@/utils/circuits/prices/formatPrice';
import { EstimateFormData } from '@/types/estimates/EstimateFormData';
import { getCombinationsFromHotelCategory } from '@/utils/estimates/hotel/getCombinationsFromHotelCategory';
import { getHotelCategoryWithOnlyOneRoom } from '@/utils/estimates/hotel/computeHotelRoomPrice';
import {
  getHotelPensionTypeLabels,
  toHotelPensionType,
} from '@/constants/hotelPension';
import { useConvertPrice } from './useCurrencyExchange';
import { useTranslation } from '@/i18n';
import {
  resolvePensionPricings,
  getRepresentativePensionPrice,
  computeHotelPensionPriceForStay,
  getPensionTypesAbove,
} from '@/utils/estimates/hotel/computeHotelPensionPrice';
import { computeHotelSupplementPriceForStay } from '@/utils/estimates/hotel/computeHotelSupplementPrice';
import type {
  HotelSupplement,
  HotelPensionType,
} from '@/types/benefits/hotel/HotelResponse';
import { SupportedLocale } from '@/types/supported-locale.type';
import { type Room } from '@/utils/estimates/findAllRoomCombinations';

type HotelRoomCategory =
  Required<BenefitFormData>['hotel']['HotelRoomCategory'][number];

export type HotelRoomCategoryTranslation = {
  name?: string;
  shortDescription?: string;
  longDescription?: string;
};

type HotelRoomCategoryWithTranslations = HotelRoomCategory & {
  translations?: { [key in SupportedLocale]?: HotelRoomCategoryTranslation };
};

const getCombinationPreferenceScore = (
  roomCombination: Room[],
  totalGuests: number,
): number => {
  const roomSizes = roomCombination
    .map((room) => room[0] + room[1] + room[2])
    .toSorted((a, b) => a - b);

  switch (totalGuests) {
    case 2: {
      // Prefer a single double room over two singles (if both options exist)
      if (roomSizes.length === 1 && roomSizes[0] === 2) return 0;
      return 1;
    }
    case 4: {
      // Prefer a quadruple or two doubles equally (cheapest wins)
      if (roomSizes.length === 1 && roomSizes[0] === 4) return 0;
      if (roomSizes.length === 2 && roomSizes[0] === 2 && roomSizes[1] === 2)
        return 0;
      return 1;
    }
    case 8: {
      // Prefer two quadruples or four doubles equally (cheapest wins)
      if (roomSizes.length === 2 && roomSizes[0] === 4 && roomSizes[1] === 4)
        return 0;
      if (roomSizes.length === 4 && roomSizes.every((size) => size === 2))
        return 0;
      return 1;
    }
    default:
      // No special preference for other totals
      return 1;
  }
};

export interface EstimateHotelCategoriesProps {
  hotelBenefit: CircuitDayFormData['steps'][number]['accommodations']['standardBenefit'];
  integrationPensionType?: string | null;
  computeBenefitPriceDelta: (optionalBenefit: BenefitFormData) => number;
  computeHotelRoomPriceForAllDates: (
    benefit: BenefitFormData,
    nbRoomAdults: number,
    nbRoomChildren: number,
    nbRoomAdolescents: number,
    selectedHotelPensionType?: string,
  ) => number;
  defaultSelectedOption: SelectedEstimateOption | null;
  adultParticipants: EstimateFormData['adults'];
  childParticipants: EstimateFormData['children'];
  standardBenefitPrice: number;
  nbDays: number;
  startDate?: Date;
  currency?: string;
  isStandardBenefit?: boolean;
  language?: SupportedLocale;
}

export const useEstimateHotelCategories = ({
  hotelBenefit,
  integrationPensionType,
  computeBenefitPriceDelta,
  defaultSelectedOption,
  adultParticipants,
  childParticipants,
  standardBenefitPrice,
  nbDays,
  startDate,
  currency = 'EUR',
  computeHotelRoomPriceForAllDates,
  language,
}: EstimateHotelCategoriesProps) => {
  const { t } = useTranslation();
  const benefitDeltaPrice = useMemo(
    () =>
      hotelBenefit ? roundUpPrice(computeBenefitPriceDelta(hotelBenefit)) : 0,
    [computeBenefitPriceDelta, hotelBenefit],
  );

  const { nbChildren, nbAdolescents, nbAdults } = useMemo(() => {
    const children = childParticipants.filter(({ birthDate }) => {
      if (
        !hotelBenefit?.hotel ||
        !hotelBenefit?.hotel.acceptsChildPricing ||
        !hotelBenefit?.hotel.childAgeFrom ||
        !hotelBenefit?.hotel.childAgeTo ||
        !birthDate
      )
        return false;
      const age = differenceInYears(startOfToday(), birthDate);
      return (
        age <= hotelBenefit?.hotel.childAgeTo &&
        age >= hotelBenefit?.hotel.childAgeFrom
      );
    }).length;

    const adolescents = childParticipants.filter(({ birthDate }) => {
      if (
        !hotelBenefit?.hotel ||
        !hotelBenefit?.hotel.acceptsChildPricing ||
        !hotelBenefit?.hotel.secondChildAgeFrom ||
        !hotelBenefit?.hotel.secondChildAgeTo ||
        !birthDate
      )
        return false;
      const age = differenceInYears(startOfToday(), birthDate);
      return (
        age <= hotelBenefit?.hotel.secondChildAgeTo &&
        age >= hotelBenefit?.hotel.secondChildAgeFrom
      );
    }).length;

    const adults =
      adultParticipants.length +
      childParticipants.length -
      children -
      adolescents;

    return {
      nbChildren: children,
      nbAdolescents: adolescents,
      nbAdults: adults,
    };
  }, [adultParticipants.length, childParticipants, hotelBenefit?.hotel]);

  const allHotelCategories = useMemo(() => {
    if (!hotelBenefit?.hotel) return [];

    const categories = hotelBenefit.hotel.HotelRoomCategory;
    const translations = language
      ? hotelBenefit.translations?.[language]
      : undefined;
    const translatedCategories = translations?.hotel?.HotelRoomCategory ?? [];

    return categories
      .map((category, categoryIndex) => {
        if (!hotelBenefit) return null;
        const benefitWithOnlyThisCategory = {
          ...hotelBenefit,
          hotel: {
            ...hotelBenefit.hotel,
            commonPensionPricings:
              hotelBenefit.hotel?.commonPensionPricings ?? [],
            HotelRoomCategory: [category],
            hotelSupplements: hotelBenefit.hotel?.hotelSupplements ?? [],
            associatedBenefits: hotelBenefit.hotel?.associatedBenefits ?? [],
          },
        } satisfies BenefitFormData;
        const deltaPrice = roundUpPrice(
          computeBenefitPriceDelta(benefitWithOnlyThisCategory),
        );
        const translatedCategory = translatedCategories[categoryIndex];
        return {
          deltaPrice,
          category: {
            ...category,
            translation:
              language && translatedCategory ? translatedCategory : undefined,
          },
        };
      })
      .filter((v) => v != null)
      .toSorted(
        ({ deltaPrice: price1 }, { deltaPrice: price2 }) => price1 - price2,
      );
  }, [computeBenefitPriceDelta, hotelBenefit, language]);

  const hotelStandardCategory = useMemo(
    () =>
      allHotelCategories.find(
        ({ deltaPrice }) => deltaPrice === benefitDeltaPrice,
      ),
    [allHotelCategories, benefitDeltaPrice],
  );

  const defaultSelectedCategory = useMemo(
    () =>
      defaultSelectedOption
        ? allHotelCategories.find(
            ({ category: { id } }) =>
              id === defaultSelectedOption.hotelCategoryId,
          )?.category
        : null,
    [allHotelCategories, defaultSelectedOption],
  );

  const [selectedHotelCategory, setSelectedHotelCategory] = useState<
    HotelRoomCategoryWithTranslations | undefined
  >(defaultSelectedCategory ?? hotelStandardCategory?.category);

  const allRoomCombinations = useMemo(
    () =>
      selectedHotelCategory
        ? getCombinationsFromHotelCategory(
            selectedHotelCategory,
            nbAdults,
            nbChildren,
            nbAdolescents,
          )
        : [],
    [nbAdolescents, nbAdults, nbChildren, selectedHotelCategory],
  );

  const allRoomPrices = useMemo(() => {
    const roomCombinationSizeKeys: Set<string> = new Set(); // To avoid duplicate combinations (even if room types are different)
    const totalGuests = nbAdults + nbChildren + nbAdolescents;

    return selectedHotelCategory
      ? allRoomCombinations
          .map((roomCombination) => {
            if (!hotelBenefit) return null;
            const combinationPrice = roomCombination.reduce((acc, room) => {
              const roomTotalPrice = hotelBenefit
                ? computeHotelRoomPriceForAllDates(
                    {
                      ...hotelBenefit,
                      hotel: {
                        ...hotelBenefit.hotel,
                        commonPensionPricings:
                          hotelBenefit.hotel?.commonPensionPricings ?? [],
                        HotelRoomCategory: [
                          getHotelCategoryWithOnlyOneRoom(
                            selectedHotelCategory,
                            room,
                          ),
                        ],
                        hotelSupplements:
                          hotelBenefit.hotel?.hotelSupplements ?? [],
                        associatedBenefits:
                          hotelBenefit.hotel?.associatedBenefits ?? [],
                      },
                    } satisfies BenefitFormData,
                    room[0],
                    room[1],
                    room[2],
                    integrationPensionType ??
                      hotelBenefit.hotel?.pricingPensionType ??
                      undefined,
                  )
                : 0;
              return acc + roomTotalPrice;
            }, 0);
            return {
              combinationPrice: sanitizeDeltaPrice(
                combinationPrice - standardBenefitPrice,
              ),
              roomCombination,
            };
          })
          .filter((v) => v != null)
          .toSorted(
            (
              { combinationPrice: price1, roomCombination: comb1 },
              { combinationPrice: price2, roomCombination: comb2 },
            ) => {
              const pref1 = getCombinationPreferenceScore(comb1, totalGuests);
              const pref2 = getCombinationPreferenceScore(comb2, totalGuests);

              if (pref1 !== pref2) return pref1 - pref2;
              return price1 - price2;
            },
          )
          .filter((combination) => {
            const sizeKey = combination.roomCombination
              .map((room) => room[0] + room[1] + room[2])
              .toSorted()
              .join(',');
            if (roomCombinationSizeKeys.has(sizeKey)) return false;
            roomCombinationSizeKeys.add(sizeKey);
            return true;
          })
      : [];
  }, [
    allRoomCombinations,
    computeHotelRoomPriceForAllDates,
    hotelBenefit,
    nbAdolescents,
    nbAdults,
    nbChildren,
    selectedHotelCategory,
    standardBenefitPrice,
  ]);

  const images = useMemo(() => {
    const standardImages = hotelBenefit?.hotel?.images ?? [];
    return hotelBenefit?.hotel?.mainImage
      ? [hotelBenefit?.hotel.mainImage, ...standardImages]
      : standardImages;
  }, [hotelBenefit?.hotel]);

  const mainImageKey = useMemo(() => {
    return images.length > 0 ? images[0] : null;
  }, [images]);

  const { convertPrice } = useConvertPrice();

  const standardPension = useMemo(
    () =>
      toHotelPensionType(
        integrationPensionType ??
          hotelBenefit?.hotel?.pricingPensionType ??
          undefined,
      ) ?? 'SingleRoom',
    [hotelBenefit?.hotel?.pricingPensionType, integrationPensionType],
  );

  const hotelPensionTypeLabels = useMemo(
    () => getHotelPensionTypeLabels(t),
    [t],
  );

  const hasSupplements =
    !!(
      hotelBenefit?.hotel?.shouldFillHotelSupplements &&
      hotelBenefit?.hotel?.hotelSupplements?.length
    ) && !!startDate;
  const supplements = useMemo(
    () => (hotelBenefit?.hotel?.hotelSupplements ?? []) as HotelSupplement[],
    [hotelBenefit?.hotel?.hotelSupplements],
  );

  const pensions = useMemo(() => {
    if (!hotelBenefit?.hotel?.shouldFillOtherPensionTypes) return [];
    const hotelBenefitCurrency = hotelBenefit?.currency ?? 'EUR';
    const pensionReferenceValue =
      integrationPensionType ?? hotelBenefit?.hotel?.pricingPensionType;

    const availableTypes = getPensionTypesAbove(
      pensionReferenceValue ?? undefined,
    );

    const pricings = resolvePensionPricings(
      hotelBenefit?.hotel?.commonPensionPricings,
      selectedHotelCategory?.categoryPensionPricings,
      hotelBenefit?.hotel?.areOtherPensionPricesCommon ?? false,
    );

    const supplementTotalForBase = hasSupplements
      ? computeHotelSupplementPriceForStay({
          supplements,
          pensionType: standardPension,
          startDate: startDate!,
          nbDays,
          nbAdults,
          nbChildren,
          nbAdolescents,
          convertPrice,
          fromCurrency: hotelBenefitCurrency,
          toCurrency: currency,
        })
      : 0;

    const computePensionTotalForType = (pensionType: HotelPensionType) => {
      if (startDate) {
        return computeHotelPensionPriceForStay({
          pricings,
          pensionType,
          startDate,
          nbDays,
          nbAdults,
          nbChildren,
          nbAdolescents,
          convertPrice,
          fromCurrency: hotelBenefitCurrency,
          toCurrency: currency,
        });
      }

      const rep = getRepresentativePensionPrice(pricings, pensionType);
      if (!rep) return null;

      return (
        (convertPrice(rep.adultPrice, hotelBenefitCurrency, currency) *
          nbAdults +
          convertPrice(rep.childPrice, hotelBenefitCurrency, currency) *
            nbChildren +
          convertPrice(rep.adolescentPrice, hotelBenefitCurrency, currency) *
            nbAdolescents) *
        nbDays
      );
    };

    const basePensionTotal = computePensionTotalForType(standardPension);
    if (basePensionTotal == null) return [];

    return availableTypes
      .map((pensionType) => {
        const pensionTotalForType = computePensionTotalForType(pensionType);
        if (pensionTotalForType == null) return null;
        let price = pensionTotalForType - basePensionTotal;

        if (hasSupplements && startDate) {
          const supplementTotalForType = computeHotelSupplementPriceForStay({
            supplements,
            pensionType,
            startDate,
            nbDays,
            nbAdults,
            nbChildren,
            nbAdolescents,
            convertPrice,
            fromCurrency: hotelBenefitCurrency,
            toCurrency: currency,
          });
          price += supplementTotalForType - supplementTotalForBase;
        }

        if (price <= 0) return null;

        return {
          value: pensionType,
          label: hotelPensionTypeLabels[pensionType],
          price,
        };
      })
      .filter(
        (
          v,
        ): v is {
          value: (typeof availableTypes)[number];
          label: string;
          price: number;
        } => v !== null,
      );
  }, [
    currency,
    hasSupplements,
    hotelBenefit?.currency,
    hotelBenefit?.hotel?.areOtherPensionPricesCommon,
    hotelBenefit?.hotel?.commonPensionPricings,
    hotelBenefit?.hotel?.pricingPensionType,
    hotelBenefit?.hotel?.shouldFillOtherPensionTypes,
    integrationPensionType,
    hotelPensionTypeLabels,
    convertPrice,
    nbAdolescents,
    nbAdults,
    nbChildren,
    nbDays,
    standardPension,
    startDate,
    supplements,
    selectedHotelCategory?.categoryPensionPricings,
  ]);

  const arePensionsGlobal = useMemo(
    () => hotelBenefit?.hotel?.areOtherPensionPricesCommon ?? false,
    [hotelBenefit?.hotel?.areOtherPensionPricesCommon],
  );

  return {
    allHotelCategories,
    allRoomPrices,
    selectedHotelCategory,
    setSelectedHotelCategory,
    images,
    mainImageKey,
    standardPension,
    pensions,
    arePensionsGlobal,
  };
};
